function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import _ from 'lodash';
import PropTypes from 'prop-types';
import useMobile from '../hooks/use-mobile';
import Spacer from '../spacer';
import Colors from '../colors';
import Text from '../text';
import Panel from '../panel';
import Button from '../button';
import ProgressBar from '../progress-bar';
import Icon from '../icon';
const styles = {
  title: {
    height: 24,
    lineHeight: 24
  },
  icon: {
    marginLeft: 'auto'
  },
  button: {
    request: {
      label: 'Request Advance',
      icon: 'Request',
      iconFamily: Icon.AdvanceFeatureIcon,
      width: 200,
      color: 'primary'
    },
    pay: {
      label: 'Pay Bills',
      icon: 'Bills',
      iconFamily: Icon.AdvanceFeatureIcon,
      width: 136,
      color: 'secondary'
    },
    more: {
      label: 'More',
      icon: 'more-horiz',
      iconFamily: Icon.MaterialIcons,
      width: null,
      color: 'secondary'
    }
  }
};
const StyledRow = styled.View`
  flex-direction: row;
`;
const StyledButtonContainer = styled.View`
  flex-direction: ${props => props.mobile && props.len <= 2 ? 'column' : 'row'};
  width: ${props => !props.noBar && '100%'};
  justify-content: ${props => props.mobile ? 'space-evenly' : 'flex-start'};
`;
const StyledLabelContainer = styled.View`
  justify-content: space-between;
  width: 100%;
`;
const StyledAmount = styled.View`
  margin-bottom: ${props => props.mobile || props.noBar ? 0 : 7}px;
`;
const StyledView = styled.View`
  width: 100%;
`;
const StyledSpacedRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
`;

const formatAmount = amount => {
  const decimals = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  };
  return amount.toLocaleString('en-US', decimals);
};

const getName = buttonName => {
  const defaultName = AvailableCredit.defaultProps.buttons[0].name;
  return Object.keys(styles.button).includes(buttonName) ? buttonName : defaultName;
};

const StyledLabel = label => /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
  weight: "regular",
  color: Colors.gray600
}, "\u20B1", ' ', formatAmount(label.amount));

const DefaultButton = button => {
  const StyledButton = button.variant === 'more' && !button.mobile ? Button.Icon : Button;
  const style = {
    width: button.mobile ? '100%' : styles.button[button.variant].width
  };
  return /*#__PURE__*/React.createElement(View, {
    style: style
  }, /*#__PURE__*/React.createElement(StyledButton, _extends({
    type: styles.button[button.variant].color,
    iconFamily: styles.button[button.variant].iconFamily
  }, button), button.label));
};

const MobileButton = button => button.criteria && /*#__PURE__*/React.createElement(DefaultButton, button) || /*#__PURE__*/React.createElement(Button.Vertical, _extends({
  type: "secondary",
  iconFamily: styles.button[button.variant].iconFamily
}, button), button.variant === 'request' ? 'Advance' : button.label);

const DesktopButton = button => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DefaultButton, button), /*#__PURE__*/React.createElement(Spacer, {
  size: "f16",
  width: true
}));

export default function AvailableCredit(props) {
  const mobile = useMobile();
  const progress = props.usedCredit / props.creditLimit * 100;
  const panelStyle = {
    paddingHorizontal: mobile ? 16 : 40
  };
  const currencyStyle = {
    lineHeight: mobile ? '130%' : '150%'
  };
  const CreditButtonContainer = props.noBar && !mobile ? StyledSpacedRow : StyledView;
  const CreditContainer = props.noBar && !mobile ? View : StyledView;
  const textColor = props.disabled ? Colors.gray100 : Colors.gray600;
  return /*#__PURE__*/React.createElement(Panel, {
    alignItems: "flex-start",
    style: panelStyle
  }, /*#__PURE__*/React.createElement(Spacer, {
    size: mobile ? 'f16' : 'f24',
    height: true
  }), /*#__PURE__*/React.createElement(CreditButtonContainer, null, /*#__PURE__*/React.createElement(CreditContainer, null, /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
    weight: "medium",
    color: Colors.gray400,
    style: styles.title
  }, "Available credit"), /*#__PURE__*/React.createElement(Spacer, {
    size: mobile ? 'f4' : 'f2',
    height: true
  }), /*#__PURE__*/React.createElement(StyledAmount, {
    mobile: mobile,
    noBar: props.noBar
  }, /*#__PURE__*/React.createElement(StyledRow, null, /*#__PURE__*/React.createElement(Text.H4, {
    color: textColor,
    style: currencyStyle
  }, "\u20B1"), /*#__PURE__*/React.createElement(Spacer, {
    size: mobile ? 'f4' : 'f8',
    width: true
  }), /*#__PURE__*/React.createElement(Text.H2, {
    color: textColor
  }, formatAmount(props.usedCredit))))), !props.noBar && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ProgressBar, {
    color: Colors.indigo400,
    progress: progress
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    height: true
  }), /*#__PURE__*/React.createElement(StyledLabelContainer, null, /*#__PURE__*/React.createElement(StyledRow, null, /*#__PURE__*/React.createElement(StyledLabel, {
    amount: props.minLoanAmount
  }), /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: "info",
    color: Colors.gray100,
    size: 12,
    style: styles.icon,
    onPress: props.showInfo
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f4",
    width: true
  }), /*#__PURE__*/React.createElement(StyledLabel, {
    amount: props.creditLimit
  }))), mobile && props.buttons.length <= 2 && /*#__PURE__*/React.createElement(Spacer, {
    size: "f16",
    height: true
  }) || /*#__PURE__*/React.createElement(Spacer, {
    size: "f24",
    height: true
  })), mobile && /*#__PURE__*/React.createElement(React.Fragment, null, props.children, props.buttons.length <= 2 && /*#__PURE__*/React.createElement(Spacer, {
    size: "f16",
    height: true
  }) || /*#__PURE__*/React.createElement(Spacer, {
    size: "f24",
    height: true
  })), /*#__PURE__*/React.createElement(StyledButtonContainer, {
    mobile: mobile,
    noBar: props.noBar,
    len: props.buttons.length
  }, _.slice(props.buttons, 0, 3).map((button, i) => mobile && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MobileButton, _extends({
    key: i,
    criteria: props.buttons.length <= 2,
    label: styles.button[getName(button.name)].label,
    mobile: mobile,
    variant: getName(button.name),
    icon: styles.button[getName(button.name)].icon
  }, button)), i < 3 && /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    height: true
  })) || button && /*#__PURE__*/React.createElement(DesktopButton, _extends({
    key: i,
    label: styles.button[getName(button.name)].label,
    mobile: mobile,
    variant: getName(button.name),
    icon: styles.button[getName(button.name)].icon
  }, button))))), !mobile && props.children, /*#__PURE__*/React.createElement(Spacer, {
    size: mobile || props.disabled ? 'f24' : 'd40',
    height: true
  }));
}
AvailableCredit.defaultProps = {
  usedCredit: 0,
  creditLimit: 0,
  minLoanAmount: 0,
  buttons: [{
    name: 'request',
    onPress: () => {},
    disabled: false
  }],
  children: '',
  disabled: false,
  noBar: false,
  showInfo: () => {}
};
AvailableCredit.propTypes = {
  usedCredit: PropTypes.number,
  creditLimit: PropTypes.number,
  minLoanAmount: PropTypes.number,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.oneOf(Object.keys(styles.button)),
    onPress: PropTypes.func,
    disabled: PropTypes.bool
  })),
  children: PropTypes.node,
  disabled: PropTypes.bool,
  noBar: PropTypes.bool,
  showInfo: PropTypes.func
};
//# sourceMappingURL=available-credit.js.map